import {
  Anchor,
  Box,
  Button,
  Group,
  Loader,
  LoadingOverlay,
  Modal,
  Paper,
  Radio,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconDeviceFloppy, IconExternalLink } from '@tabler/icons-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { AIInputWrapper } from '@/components/AIInputWrapper'
import { InputLabel } from '@/components/InputLabel'
import { MarkdownEditor } from '@/components/MarkdownEditor'
import { RadioCard } from '@/components/RadioCard'
import { theme } from '@/configs/theme'
import { useGeneralInfoAIField } from '@/hooks/useGeneralInfoAIField'
import { useGlobalState } from '@/hooks/useGlobalState'
import { UploadedFilePreview } from '@/screens/ContentModuleFormScreen/UploadedFilePreview'
import type { GeneralInfoContent, GeneralInfoResource, ResourceType } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'

type ContentAccessType = 'full_access' | 'information_only' | 'media_only'

const COLLAPSE_WHITESPACE = (raw: string) => raw.replace(/\n\s*\n/g, '\n\n')

interface GeneralInfoEditModalProps {
  opened: boolean
  onClose: () => void
  contentId: string
}

export function GeneralInfoEditModal({ opened, onClose, contentId }: GeneralInfoEditModalProps) {
  const { api } = useGlobalState()

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [contentAccessType, setContentAccessType] = useState<ContentAccessType>('information_only')
  const [initialized, setInitialized] = useState(false)

  const titleAI = useGeneralInfoAIField({
    contentId,
    systemPromptTemplateId: 'generate_gi_title',
    value: title,
    setValue: setTitle,
  })

  const descriptionAI = useGeneralInfoAIField({
    contentId,
    systemPromptTemplateId: 'generate_gi_description',
    value: description,
    setValue: setDescription,
    postProcess: COLLAPSE_WHITESPACE,
  })

  const contentQuery = useQuery({
    queryKey: ['general-info', 'detail', contentId],
    queryFn: () => api.getGeneralInfo(contentId),
    enabled: opened && !!contentId,
    refetchOnMount: 'always',
    staleTime: 0,
  })

  const data: GeneralInfoContent | undefined = contentQuery.data
  const sourceResource: GeneralInfoResource | undefined = data?.resources.find(r => r.isSourceFile)

  useEffect(() => {
    if (!opened || !data || initialized) return

    const hasConfirmedTitle = !!sourceResource?.title?.trim()
    const hasConfirmedDesc = !!sourceResource?.description?.trim()

    if (hasConfirmedTitle) {
      setTitle(sourceResource!.title!.trim())
    } else if (sourceResource?.titleAi?.trim()) {
      setTitle('')
      titleAI.seedSuggestion(sourceResource.titleAi.trim())
    }

    if (hasConfirmedDesc) {
      setDescription(sourceResource!.description!.trim())
    } else if (sourceResource?.descriptionAi?.trim()) {
      setDescription('')
      descriptionAI.seedSuggestion(sourceResource.descriptionAi.trim())
    }

    if (!sourceResource?.showInAppSupported) {
      setContentAccessType('information_only')
    } else {
      const resourceShowInApp = data.showInApp ?? false
      const dataUseInRag = data.useInRag ?? true
      if (resourceShowInApp && dataUseInRag) {
        setContentAccessType('full_access')
      } else if (resourceShowInApp && !dataUseInRag) {
        setContentAccessType('media_only')
      } else {
        setContentAccessType('information_only')
      }
    }

    setInitialized(true)
  }, [data, sourceResource, initialized, opened, titleAI.seedSuggestion, descriptionAI.seedSuggestion])

  useEffect(() => {
    if (!opened) {
      setInitialized(false)
      setTitle('')
      setDescription('')
      setContentAccessType('information_only')
      titleAI.reset()
      descriptionAI.reset()
      titleAutoGenRef.current = null
      descAutoGenRef.current = null
    }
  }, [opened, titleAI.reset, descriptionAI.reset])

  const updateMutation = useMutation({
    mutationFn: (params: Parameters<typeof api.updateGeneralInfo>[1]) => api.updateGeneralInfo(contentId, params),
    onSuccess: () => {
      api.queryClient.invalidateQueries({ queryKey: ['general-info', 'list'] })
      api.queryClient.invalidateQueries({ queryKey: ['general-info', 'detail', contentId] })
      notifications.show({
        title: 'Content Published',
        message: 'Your content has been saved and published.',
        color: 'green',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      onClose()
    },
    onError: () => {
      notifications.show({
        title: 'Save Failed',
        message: 'Could not save the content. Please try again.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
  })

  const handleSave = useCallback(() => {
    if (!title.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Title is required.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      return
    }

    if (!description.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Description is required.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      return
    }

    const showInApp = contentAccessType === 'full_access' || contentAccessType === 'media_only'
    const useInRag = contentAccessType === 'full_access' || contentAccessType === 'information_only'

    updateMutation.mutate({
      title: title.trim(),
      description: description.trim(),
      show_in_app: showInApp,
      use_in_rag: useInRag,
    })
  }, [title, description, contentAccessType, updateMutation])

  const extractedResource = data?.resources.find(r => !r.isSourceFile)

  const extractedContentQuery = useQuery({
    queryKey: ['general-info', 'extracted-content', contentId, extractedResource?.resourceId],
    queryFn: async () => {
      if (data?.content) return data.content
      if (extractedResource?.data) return extractedResource.data
      if (extractedResource?.resourceKey) {
        return (await api.download({ file_path: extractedResource.resourceKey })) as string
      }
      return ''
    },
    enabled: !!data && (!!extractedResource || !!data.content),
  })

  const CONTENT_PREVIEW_LIMIT = 2000
  const extractedContentPreview = useMemo(() => {
    const raw = extractedContentQuery.data ?? ''
    if (raw.length <= CONTENT_PREVIEW_LIMIT) return raw
    return raw.slice(0, CONTENT_PREVIEW_LIMIT) + '\u2026'
  }, [extractedContentQuery.data])

  const hasExtractedContent = !!extractedContentQuery.data?.trim()
  const isSaving = updateMutation.isPending

  const isYoutube = sourceResource?.type === 'youtube'
  const showTranscript = !isYoutube || contentAccessType !== 'media_only'
  const fieldsDisabled = isYoutube && contentAccessType !== 'media_only' ? !hasExtractedContent : isSaving
  const aiDisabled = isSaving || (isYoutube && (!hasExtractedContent || contentAccessType === 'media_only'))

  const titleAutoGenRef = useRef<string | null>(null)
  const descAutoGenRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isYoutube || !hasExtractedContent || !initialized) return
    const genKey = `${contentId}-title`
    if (titleAutoGenRef.current === genKey) return
    titleAutoGenRef.current = genKey
    if (title.trim()) return
    if (titleAI.fieldState.state !== 'idle') return
    titleAI.generate()
  }, [isYoutube, hasExtractedContent, initialized, contentId, title, titleAI.fieldState.state, titleAI.generate])

  useEffect(() => {
    if (!isYoutube || !hasExtractedContent || !initialized) return
    const genKey = `${contentId}-description`
    if (descAutoGenRef.current === genKey) return
    descAutoGenRef.current = genKey
    if (description.trim()) return
    if (descriptionAI.fieldState.state !== 'idle') return
    descriptionAI.generate()
  }, [
    isYoutube,
    hasExtractedContent,
    initialized,
    contentId,
    description,
    descriptionAI.fieldState.state,
    descriptionAI.generate,
  ])

  const [editContentOpened, editContentHandlers] = useDisclosure(false)
  const contentEditRef = useRef('')

  const contentUpdateMutation = useMutation({
    mutationFn: (text: string) => api.updateGeneralInfo(contentId, { content: text }),
    onSuccess: (_data, savedText) => {
      api.queryClient.setQueryData(
        ['general-info', 'extracted-content', contentId, extractedResource?.resourceId],
        savedText,
      )
      api.queryClient.invalidateQueries({ queryKey: ['general-info', 'detail', contentId] })
      api.queryClient.invalidateQueries({ queryKey: ['general-info', 'list'] })
      notifications.show({
        title: 'Content Updated',
        message: 'Extracted content has been saved.',
        color: 'green',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      editContentHandlers.close()
    },
    onError: () => {
      notifications.show({
        title: 'Save Failed',
        message: 'Could not save the content. Please try again.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
  })

  const handleSaveContent = useCallback(() => {
    const text = contentEditRef.current
    if (!text.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: "Content can't be blank.",
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      return
    }
    contentUpdateMutation.mutate(text)
  }, [contentUpdateMutation])

  const isLoading = contentQuery.isLoading
  const isContentSaving = contentUpdateMutation.isPending

  const isUrlType = sourceResource?.type === 'url' || sourceResource?.type === 'youtube'
  const hasUploadedFile = sourceResource && !isUrlType && sourceResource.type !== 'text'
  const showAccessTypePicker = !!sourceResource?.showInAppSupported

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title="Edit & Configure"
        size={theme.rem(1200)}
        centered
        styles={{
          body: { paddingTop: theme.rem(16), backgroundColor: theme.colors.gray[0] },
        }}>
        <Stack bg={theme.colors.gray[0]} gap="xl" pb="xl" pos="relative">
          <LoadingOverlay visible={isLoading} />

          {/* Source File Preview */}
          {hasUploadedFile && sourceResource && (
            <Paper shadow="sm" p="md" radius="md">
              <Text size="lg" fw={600} mb="xs">
                Preview Source Content
              </Text>
              <UploadedFilePreview
                fileName={sourceResource.fileName || data?.fileName || 'Uploaded file'}
                type={sourceResource.type as ResourceType}
                resourceKey={sourceResource.resourceKey}
                resourceData={sourceResource.data}
              />
            </Paper>
          )}

          {isUrlType && sourceResource && (
            <Paper shadow="sm" p="md" radius="md">
              <Text size="lg" fw={600} mb="sm">
                {isYoutube ? 'Preview Media' : 'Preview Source Content'}
              </Text>
              <Paper withBorder p="sm" radius="sm" bg="gray.1">
                {sourceResource.data || sourceResource.sourceUrl ? (
                  <Anchor href={sourceResource.data || sourceResource.sourceUrl || ''} target="_blank" size="sm">
                    <Group gap={6}>
                      {sourceResource.data || sourceResource.sourceUrl}
                      <IconExternalLink size={14} />
                    </Group>
                  </Anchor>
                ) : (
                  <Text c="dimmed" size="sm">
                    URL unavailable
                  </Text>
                )}
              </Paper>
            </Paper>
          )}

          {/* Extracted Content (non-YouTube) */}
          {!isYoutube && (
            <Paper shadow="sm" p="md" radius="md">
              <Stack gap="xs">
                <Group justify="space-between">
                  <InputLabel label="Extracted Content" />
                  <Button
                    variant="light"
                    size="md"
                    radius="md"
                    onClick={() => {
                      contentEditRef.current = extractedContentQuery.data ?? ''
                      editContentHandlers.open()
                    }}
                    disabled={!hasExtractedContent || isSaving}>
                    Edit
                  </Button>
                </Group>
                {extractedContentQuery.isLoading ? (
                  <Group justify="center" py="xl">
                    <Loader size="sm" />
                    <Text size="sm" c="dimmed">
                      Loading extracted content...
                    </Text>
                  </Group>
                ) : (
                  <Textarea
                    value={extractedContentPreview}
                    disabled
                    placeholder={
                      data?.inProcess ? 'Content is still being processed...' : 'No extracted content available'
                    }
                    radius="md"
                    variant="filled"
                    size="sm"
                    rows={6}
                    style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: theme.radius.md }}
                  />
                )}
              </Stack>
            </Paper>
          )}

          {/* Edit & Configure */}
          <Paper shadow="sm" p="md" radius="md">
            <Stack gap="xxl">
              <Text size="lg" fw={600}>
                {isYoutube ? 'Edit & Configure Media' : 'Edit & Configure Content'}
              </Text>

              {showAccessTypePicker && (
                <Radio.Group
                  label="Content Access"
                  description="Choose how this content will be used."
                  value={contentAccessType}
                  onChange={v => v && setContentAccessType(v as ContentAccessType)}>
                  <Stack gap="sm" mt="xs">
                    <RadioCard
                      label="Full Access"
                      description={
                        isYoutube
                          ? "Sidekick can use the transcript to answer questions and can show the original YouTube video when it's relevant."
                          : "Sidekick can use the extracted content to answer questions and can show the original file (such as a video or PDF) when it's relevant."
                      }
                      value="full_access"
                    />
                    <RadioCard
                      label="Information Only"
                      description={
                        isYoutube
                          ? "Sidekick can use the transcript to answer questions, but it won't show the original YouTube video."
                          : "Sidekick can use the extracted content to answer questions, but it won't show the original file."
                      }
                      value="information_only"
                    />
                    <RadioCard
                      label="Media Only"
                      description={
                        isYoutube
                          ? "Sidekick can show the YouTube video, but it won't use the transcript to answer questions."
                          : "Sidekick can show the original file, but it won't use the extracted content to answer questions."
                      }
                      value="media_only"
                    />
                  </Stack>
                </Radio.Group>
              )}

              {/* Transcript (YouTube only, above title/description) */}
              {isYoutube && showTranscript && (
                <Stack gap="xs">
                  <Group justify="space-between">
                    <InputLabel label="Transcript" withAsterisk />
                    <Button
                      variant="light"
                      size="md"
                      radius="md"
                      onClick={() => {
                        contentEditRef.current = extractedContentQuery.data ?? ''
                        editContentHandlers.open()
                      }}
                      disabled={isSaving}>
                      Edit
                    </Button>
                  </Group>
                  {extractedContentQuery.isLoading ? (
                    <Group justify="center" py="xl">
                      <Loader size="sm" />
                      <Text size="sm" c="dimmed">
                        Loading transcript...
                      </Text>
                    </Group>
                  ) : (
                    <Textarea
                      value={extractedContentPreview}
                      readOnly
                      placeholder="Add the transcript for your media so the Sidekick can navigate the media"
                      radius="md"
                      variant="filled"
                      size="sm"
                      rows={6}
                      style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: theme.radius.md }}
                    />
                  )}
                </Stack>
              )}

              <AIInputWrapper
                fieldState={titleAI.fieldState}
                onAskSidekick={titleAI.generate}
                onAskSidekickWithGuide={titleAI.generateWithGuide}
                onAccept={titleAI.accept}
                onReject={titleAI.reject}
                onRegenerate={titleAI.generate}
                onCancel={titleAI.cancel}
                disabled={aiDisabled}>
                <TextInput
                  withAsterisk
                  label="Title"
                  radius="md"
                  value={title}
                  onChange={e => setTitle(e.currentTarget.value)}
                  disabled={fieldsDisabled}
                />
              </AIInputWrapper>

              <AIInputWrapper
                fieldState={descriptionAI.fieldState}
                onAskSidekick={descriptionAI.generate}
                onAskSidekickWithGuide={descriptionAI.generateWithGuide}
                onAccept={descriptionAI.accept}
                onReject={descriptionAI.reject}
                onRegenerate={descriptionAI.generate}
                onCancel={descriptionAI.cancel}
                disabled={aiDisabled}>
                <Textarea
                  label="Description"
                  withAsterisk
                  placeholder="Enter a description for this content"
                  value={description}
                  onChange={e => setDescription(e.currentTarget.value)}
                  minRows={3}
                  autosize
                  disabled={fieldsDisabled}
                />
              </AIInputWrapper>
            </Stack>
          </Paper>

          <Group gap="lg" justify="flex-end">
            <Button variant="light" color="gray" radius="md" disabled={isSaving} onClick={onClose}>
              Cancel
            </Button>

            <Button
              leftSection={<IconDeviceFloppy />}
              onClick={handleSave}
              disabled={isSaving}
              loading={isSaving}
              radius="md">
              Save and Publish
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={editContentOpened}
        onClose={editContentHandlers.close}
        title="Edit File Content Manually"
        size="95vw"
        centered
        styles={{
          body: { paddingTop: theme.rem(16) },
        }}>
        <Box className={commonClasses.stickyModalFooterContentWrapper}>
          <MarkdownEditor
            defaultValue={contentEditRef.current}
            onChange={v => {
              contentEditRef.current = v
            }}
            readOnly={isContentSaving}
            className={clsx(isContentSaving && 'opacity-60 pointer-events-none')}
            style={{ minHeight: '50vh' }}
          />
        </Box>

        <Modal.Header className={commonClasses.stickyModalFooter}>
          <Group gap="xl">
            <Button
              leftSection={<IconDeviceFloppy />}
              onClick={handleSaveContent}
              loading={isContentSaving}
              disabled={isContentSaving}>
              Save Content
            </Button>
            {!isContentSaving && (
              <Button size="sm" variant="light" color="gray" onClick={editContentHandlers.close}>
                Cancel
              </Button>
            )}
          </Group>
        </Modal.Header>
      </Modal>
    </>
  )
}
